import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Collapse,
  Flex,
  Heading,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useColorMode,
  VStack,
  HStack,
  Badge,
  Card,
  CardBody,
  Icon,
  useColorModeValue,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Divider,
  useToast,
} from "@chakra-ui/react";

import { 
  ChevronDownIcon, 
  ChevronUpIcon, 
  SearchIcon, 
  CheckCircleIcon,
  TimeIcon,
  InfoIcon,
  PhoneIcon,
  CalendarIcon,
  EditIcon,
  RepeatIcon,
  ViewIcon
} from "@chakra-ui/icons";
import { 
  FiPackage, 
  FiTruck, 
  FiCheckCircle, 
  FiClock, 
  FiAlertCircle,
  FiTrendingUp,
  FiUsers,
  FiShoppingBag
} from "react-icons/fi";
import API from "../../context/API";
import DateRangePicker from "../../commonComponents/DateRangePicker";
import moment from "moment";
import Pagination from "../Common/Pagination/Pagination";
import DateSelector from "../../commonComponents/DateSelector";

const addCommasInPrices = (value) => {
  return (parseFloat(value) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTab, setSelectedTab] = useState("pending");
  const { colorMode } = useColorMode();
  const [currentpage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalpages] = useState(1);
  const [selectedDateRange, setSelectedDateRange] = useState({
    startDate: moment(),
    endDate: moment(),
  });
  const [selectedFilter, setSelectedFilter] = useState("");
  const [isFilter, setIsFilter] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const toast = useToast();

  // Color mode values
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const textColor = useColorModeValue("gray.800", "gray.100");
  const mutedText = useColorModeValue("gray.600", "gray.400");
  const accentColor = useColorModeValue("blue.500", "blue.300");
  const successColor = useColorModeValue("green.500", "green.300");
  const warningColor = useColorModeValue("orange.500", "orange.300");
  const dangerColor = useColorModeValue("red.500", "red.300");

  useEffect(() => {
    fetchOrders(selectedTab);
  }, [selectedTab, selectedDateRange, currentpage, limit, selectedFilter]);

  const fetchOrders = async (status) => {
    setLoading(true);
    try {
      let allOrders = [];
      const token = localStorage.getItem("access_token");
      const params1 = {
        type: "history",
        startDate: moment(selectedDateRange.startDate)?.format("YYYY-MM-DD"),
        endDate: moment(selectedDateRange.endDate)?.format("YYYY-MM-DD"),
        page: currentpage,
        limit: limit,
      };
      const params2 = {
        type: "history",
        filter: selectedFilter,
        page: currentpage,
        limit: limit,
      };
      if (status === "history") {
        const response = await API.get(`/orders`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: isFilter ? params2 : params1,
        });
        if (response.data.success) {
          allOrders = response.data.data.orders || [];
          setTotalpages(response.data.data.totalPages);
        }
      } else {
        const response = await API.get(`/orders?type=${status}`);
        if (response.data.success) {
          allOrders = response.data.data;
        }
      }

      setOrders(allOrders);
      setError(null); // Clear any previous errors
    } catch (error) {
      console.error("Error fetching orders:", error);
      
      // Simple error handling without referencing non-existent fields
      setError("An error occurred while fetching orders.");
      setOrders([]);
      toast({
        title: "Error",
        description: "Failed to fetch orders. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleCollapse = (id) => {
    setOrders((prevOrders) =>
      prevOrders.map((order) =>
        order.id === id
          ? { ...order, isOpen: !order.isOpen }
          : { ...order, isOpen: false }
      )
    );
  };

  const handleSelection = (selection) => {
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return "yellow";
      case "preparing":
        return "blue";
      case "ready":
        return "orange";
      case "completed":
        return "green";
      case "cancelled":
        return "red";
      default:
        return "gray";
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return FiClock;
      case "preparing":
        return FiPackage;
      case "ready":
        return FiTruck;
      case "completed":
        return FiCheckCircle;
      case "cancelled":
        return FiAlertCircle;
      default:
        return FiClock;
    }
  };

  const getOrderStats = () => {
    const totalOrders = orders.length;
    const totalRevenue = orders.reduce((sum, order) => sum + (parseFloat(order.total_price) || 0), 0);
    const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;
    
    return { totalOrders, totalRevenue, avgOrderValue };
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = searchTerm === "" || 
      order.id.toString().includes(searchTerm) ||
      order.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.user_phone?.includes(searchTerm);
    
    const matchesStatus = statusFilter === "all" || 
      order.status?.toLowerCase() === statusFilter.toLowerCase();
    
    return matchesSearch && matchesStatus;
  });

  const stats = getOrderStats();

  return (
    <Box bg={bgColor} minH="100vh" p={{ base: 4, md: 6, lg: 8 }}>
      {/* Header Section */}
      <Box mb={8}>
        <Flex direction={{ base: "column", md: "row" }} justify="space-between" align={{ base: "flex-start", md: "center" }} gap={4}>
          <VStack align={{ base: "flex-start", md: "flex-start" }} spacing={2}>
            <Heading size="xl" color={textColor} fontWeight="700">
              Order Management
            </Heading>
            <Text color={mutedText} fontSize="lg">
              Monitor and manage all customer orders
            </Text>
          </VStack>
          
          <HStack spacing={3}>
            <Button
              leftIcon={<RepeatIcon />}
              variant="outline"
              onClick={() => fetchOrders(selectedTab)}
              size="sm"
              isLoading={loading}
            >
              Refresh
            </Button>
          </HStack>
        </Flex>
      </Box>

      {/* Stats Cards */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={8}>
        <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
          <CardBody>
            <Stat>
              <StatLabel color={mutedText}>Total Orders</StatLabel>
              <StatNumber color={textColor}>{stats.totalOrders}</StatNumber>
              <StatHelpText color={mutedText}>Current period</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
        
        <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
          <CardBody>
            <Stat>
              <StatLabel color={mutedText}>Total Revenue</StatLabel>
              <StatNumber color={successColor}>${addCommasInPrices(stats.totalRevenue)}</StatNumber>
              <StatHelpText color={mutedText}>Current period</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
        
        <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
          <CardBody>
            <Stat>
              <StatLabel color={mutedText}>Avg Order Value</StatLabel>
              <StatNumber color={accentColor}>${addCommasInPrices(stats.avgOrderValue)}</StatNumber>
              <StatHelpText color={mutedText}>Current period</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
        
        <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
          <CardBody>
            <Stat>
              <StatLabel color={mutedText}>Pending Orders</StatLabel>
              <StatNumber color={warningColor}>
                {orders.filter(o => o.status?.toLowerCase() === "pending").length}
              </StatNumber>
              <StatHelpText color={mutedText}>Requires attention</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Search and Filters */}
      <Card bg={cardBg} border="1px solid" borderColor={borderColor} mb={6}>
        <CardBody>
          <Flex direction={{ base: "column", md: "row" }} gap={4} align={{ base: "stretch", md: "center" }}>
            <InputGroup maxW="300px">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color={mutedText} />
              </InputLeftElement>
              <Input
                placeholder="Search orders by ID, name, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                borderRadius="lg"
              />
            </InputGroup>
            
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              maxW="200px"
              borderRadius="lg"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="preparing">Preparing</option>
              <option value="ready">Ready</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </Select>
            
            {selectedTab === "history" && (
              <HStack spacing={2}>
                <DateRangePicker
                  selectedDateRange={selectedDateRange}
                  onDateRangeChange={(date) => setSelectedDateRange(date)}
                />
                <Box backgroundColor="white">
                  <DateSelector onDateSelect={handleSelection} />
                </Box>
              </HStack>
            )}
          </Flex>
        </CardBody>
      </Card>

      {/* Orders Tabs */}
      <Card bg={cardBg} border="1px solid" borderColor={borderColor}>
        <Tabs
          variant="enclosed"
          onChange={(index) =>
            setSelectedTab(["pending", "preparing", "completed", "ready", "history"][index])
          }
          colorScheme="blue"
        >
          <TabList>
            <Tab>
              <HStack spacing={2}>
                <Icon as={FiClock} />
                <Text>Pending</Text>
                <Badge colorScheme="yellow" variant="solid" borderRadius="full">
                  {orders.filter(o => o.status?.toLowerCase() === "pending").length}
                </Badge>
              </HStack>
            </Tab>
            <Tab>
              <HStack spacing={2}>
                <Icon as={FiPackage} />
                <Text>Preparing</Text>
                <Badge colorScheme="blue" variant="solid" borderRadius="full">
                  {orders.filter(o => o.status?.toLowerCase() === "preparing").length}
                </Badge>
              </HStack>
            </Tab>
            <Tab>
              <HStack spacing={2}>
                <Icon as={FiTruck} />
                <Text>Ready</Text>
                <Badge colorScheme="orange" variant="solid" borderRadius="full">
                  {orders.filter(o => o.status?.toLowerCase() === "ready").length}
                </Badge>
              </HStack>
            </Tab>
            <Tab>
              <HStack spacing={2}>
                <Icon as={FiCheckCircle} />
                <Text>Completed</Text>
                <Badge colorScheme="green" variant="solid" borderRadius="full">
                  {orders.filter(o => o.status?.toLowerCase() === "completed").length}
                </Badge>
              </HStack>
            </Tab>
            <Tab>
              <HStack spacing={2}>
                <Icon as={FiTrendingUp} />
                <Text>History</Text>
              </HStack>
            </Tab>
          </TabList>
          
          <TabPanels>
            {["pending", "preparing", "completed", "ready", "history"].map((tabName) => (
              <TabPanel key={tabName}>
                {loading ? (
                  <Flex justify="center" align="center" p={8}>
                    <VStack spacing={4}>
                      <Spinner size="xl" color={accentColor} />
                      <Text color={mutedText}>Loading orders...</Text>
                    </VStack>
                  </Flex>
                ) : filteredOrders.length === 0 ? (
                  <Box textAlign="center" py={12}>
                    <Icon as={FiShoppingBag} boxSize={16} color={mutedText} mb={4} />
                    <Text fontSize="lg" color={mutedText} mb={2}>
                      No {tabName} orders found
                    </Text>
                    <Text color={mutedText}>
                      {searchTerm || statusFilter !== "all" 
                        ? "Try adjusting your search or filters" 
                        : "Orders will appear here when customers place them"}
                    </Text>
                  </Box>
                ) : (
                  <>
                    <OrderTable
                      orders={filteredOrders}
                      onToggleCollapse={handleToggleCollapse}
                      colorMode={colorMode}
                      showStatus={tabName === "history"}
                      getStatusColor={getStatusColor}
                      getStatusIcon={getStatusIcon}
                    />
                    
                    {tabName === "history" && (
                      <Pagination
                        currentPage={currentpage}
                        itemsPerPage={limit}
                        totalPages={totalPages}
                        onItemsPerPageChange={(lim) => setLimit(lim)}
                        onPageChange={(page) => setCurrentPage(page)}
                      />
                    )}
                  </>
                )}
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      </Card>
    </Box>
  );
}

const user = localStorage.getItem("user");

let parsedUser;
if (user) {
  try {
    parsedUser = JSON.parse(user);
  } catch (e) {
    console.error("Failed to parse user from localStorage", e);
    parsedUser = null;
  }
} else {
  parsedUser = null;
}

const OrderTable = ({
  orders,
  onToggleCollapse,
  showStatus = false,
  colorMode,
  getStatusColor,
  getStatusIcon,
}) => {
  const bgColor = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const textColor = useColorModeValue("gray.800", "gray.100");
  const mutedText = useColorModeValue("gray.600", "gray.400");
  const accentColor = useColorModeValue("blue.500", "blue.300");
  const successColor = useColorModeValue("green.500", "green.300");
  const warningColor = useColorModeValue("orange.500", "orange.300");

  const getPhoneBlurred = (phone) => {
    if (phone.length > 4) {
      return phone.slice(0, -4);
    } else {
      return phone.slice(0, -2);
    }
  };
  
  const getPhoneUnBlurred = (phone) => {
    if (phone.length > 4) {
      return phone.slice(-4);
    } else {
      return phone.slice(-2);
    }
  };
  
  const formatDateAndTime = (isoString) => {
    const date = new Date(isoString);
    const formattedDate = date.toLocaleDateString("en-GB", { timeZone: 'UTC' });
    const formattedTime = date.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
      timeZone: 'UTC'
    });

    return { formattedDate, formattedTime };
  };

  return (
    <VStack spacing={4} align="stretch">
      {orders.map((order) => (
        <Card
          key={order.id}
          bg={cardBg}
          border="1px solid"
          borderColor={borderColor}
          borderRadius="xl"
          shadow="sm"
          _hover={{ shadow: "md" }}
          transition="all 0.2s ease"
        >
          <CardBody>
            {/* Order Header */}
            <Flex direction={{ base: "column", md: "row" }} justify="space-between" align={{ base: "flex-start", md: "center" }} gap={4} mb={4}>
              <VStack align={{ base: "flex-start", md: "flex-start" }} spacing={2}>
                <HStack spacing={3}>
                  <Badge
                    colorScheme={getStatusColor(order.status)}
                    variant="solid"
                    px={3}
                    py={1}
                    borderRadius="full"
                    fontSize="sm"
                  >
                    <Icon as={getStatusIcon(order.status)} mr={1} />
                    {order.status || "Unknown"}
                  </Badge>
                  <Text fontSize="sm" color={mutedText}>
                    #{order.id}
                  </Text>
                </HStack>
                
                <HStack spacing={4} wrap="wrap">
                  <HStack spacing={2}>
                    <Icon as={FiUsers} color={accentColor} boxSize={4} />
                    <Text fontWeight="600" color={textColor}>
                      {order.user_name || "Unknown Customer"}
                    </Text>
                  </HStack>
                  
                  <HStack spacing={2}>
                    <Icon as={PhoneIcon} color={accentColor} boxSize={4} />
                    <Text color={mutedText}>
                      <Text as="span" filter="blur(3px)" display="inline">
                        {getPhoneBlurred(order.user_phone)}
                      </Text>
                      {getPhoneUnBlurred(order.user_phone)}
                    </Text>
                  </HStack>
                  
                  <HStack spacing={2}>
                    <Icon as={CalendarIcon} color={accentColor} boxSize={4} />
                    <Text color={mutedText} fontSize="sm">
                      {formatDateAndTime(order.date).formattedDate}
                    </Text>
                  </HStack>
                  
                  <HStack spacing={2}>
                    <Icon as={TimeIcon} color={accentColor} boxSize={4} />
                    <Text color={mutedText} fontSize="sm">
                      {formatDateAndTime(order.date).formattedTime}
                    </Text>
                  </HStack>
                </HStack>
              </VStack>
              
              <VStack align={{ base: "flex-start", md: "flex-end" }} spacing={2}>
                <HStack spacing={4}>
                  <VStack spacing={1} align="center">
                    <Text fontSize="sm" color={mutedText}>Total</Text>
                    <Text fontSize="lg" fontWeight="700" color={successColor}>
                      ${addCommasInPrices((parseFloat(order.total_price) || 0).toFixed(2))}
                    </Text>
                  </VStack>
                  
                  <VStack spacing={1} align="center">
                    <Text fontSize="sm" color={mutedText}>Subtotal</Text>
                    <Text fontSize="md" fontWeight="600" color={textColor}>
                      ${addCommasInPrices((
                        (parseFloat(order.total_price) || 0) - 
                        ((parseFloat(order?.tip) || 0) +
                        ((parseFloat(order.total_price) || 0) * (parseFloat(parsedUser?.tax) || 0)))
                      ).toFixed(2))}
                    </Text>
                  </VStack>
                  
                  <VStack spacing={1} align="center">
                    <Text fontSize="sm" color={mutedText}>Tax</Text>
                    <Text fontSize="md" fontWeight="600" color={warningColor}>
                      ${addCommasInPrices(((parseFloat(order.total_price) || 0) * (parseFloat(parsedUser?.tax) || 0)).toFixed(2))}
                    </Text>
                  </VStack>
                  
                  <VStack spacing={1} align="center">
                    <Text fontSize="sm" color={mutedText}>Tip</Text>
                    <Text fontSize="md" fontWeight="600" color={accentColor}>
                      ${addCommasInPrices((parseFloat(order.tip) || 0).toFixed(2))}
                    </Text>
                  </VStack>
                </HStack>
                
                <Button
                  leftIcon={order.isOpen ? <ViewIcon /> : <EditIcon />}
                  variant="outline"
                  colorScheme="blue"
                  size="sm"
                  onClick={() => onToggleCollapse(order.id)}
                  borderRadius="lg"
                >
                  {order.isOpen ? "Hide Details" : "View Details"}
                </Button>
              </VStack>
            </Flex>
            
            {/* Order Details Collapse */}
            <Collapse in={order.isOpen}>
              <Divider my={4} />
              <Box>
                <Heading size="md" mb={4} color={textColor}>
                  Order Items
                </Heading>
                <VStack spacing={3} align="stretch">
                  {order.items?.map((item) => (
                    <Card
                      key={item.id}
                      bg={bgColor}
                      border="1px solid"
                      borderColor={borderColor}
                      borderRadius="lg"
                    >
                      <CardBody p={4}>
                        <Flex justify="space-between" align="center" mb={2}>
                          <Text fontWeight="600" color={textColor}>
                            {item.item?.item || "Unknown Item"}
                          </Text>
                          <HStack spacing={3}>
                            <Badge colorScheme="blue" variant="subtle">
                              Qty: {item.quantity}
                            </Badge>
                            <Text fontWeight="600" color={successColor}>
                              ${addCommasInPrices((parseFloat(item.itemPrice) || 0).toFixed(2))}
                            </Text>
                          </HStack>
                        </Flex>
                        
                        {item.orderItemCustomizations?.length > 0 && (
                          <VStack spacing={2} align="stretch" mt={3}>
                            <Text fontSize="sm" color={mutedText} fontWeight="500">
                              Customizations:
                            </Text>
                            {item.orderItemCustomizations.map((customization) => (
                              <HStack
                                key={customization.id}
                                spacing={3}
                                pl={4}
                                borderLeft="2px solid"
                                borderColor={accentColor}
                              >
                                <Text fontSize="sm" color={textColor}>
                                  {customization.customization?.customization}:
                                </Text>
                                <Text fontSize="sm" color={mutedText}>
                                  {customization.item?.item}
                                </Text>
                                <Badge colorScheme="green" variant="subtle" size="sm">
                                  +${addCommasInPrices((parseFloat(customization.itemPrice) || 0).toFixed(2))}
                                </Badge>
                              </HStack>
                            ))}
                          </VStack>
                        )}
                      </CardBody>
                    </Card>
                  ))}
                </VStack>
              </Box>
            </Collapse>
          </CardBody>
        </Card>
      ))}
    </VStack>
  );
};

export default Orders;
